import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './contexts/AuthContext'
import Footer from './shared/components/layout/Footer'
import ScrollToTop from './shared/components/scroll/ScrollToTop'
import LoadingScreen from './shared/components/loading/LoadingScreen'
import ErrorBoundary from './shared/error/ErrorBoundary'
import { AlertProvider } from './contexts/AlertContext'
import GlobalAlert from './shared/components/alerts/GlobalAlert'
import { HelpAssistantProvider } from './contexts/HelpAssistantContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { ThemeProvider } from './contexts/ThemeContext'

import './App.css'
import './shared/css/UIPrimitives.css'
import './shared/css/CardsPrimitives.css'
import './shared/css/FormPrimitives.css'
import './i18n'

/* ─────────────  Lazy-loaded pages  ───────────── */
const LandingPage = lazy(() => import('./features/home/pages/LandingPage'))
const ContactPage = lazy(() => import('./features/home/pages/ContactPage'))
const LoginPage = lazy(() => import('./features/auth/pages/LoginPage'))
/*  const RegisterPage = lazy(() => import('./features/auth/pages/RegisterPage')) */
const TermsPage = lazy(() => import('./features/auth/pages/TermsPage'))
const PrivacyPolicyPage = lazy(
  () => import('./features/auth/pages/PrivacyPolicyPage'),
)
const CookiesPolicyPage = lazy(
  () => import('./features/auth/pages/CookiesPolicyPage'),
)
const DashboardPage = lazy(
  () => import('./features/dashboard/pages/DashboardPage'),
)
const WeatherPage = lazy(() => import('./features/weather/pages/WeatherPage'))
const PricesPage = lazy(() => import('./features/prices/pages/PricesPage'))
const MapPage = lazy(() => import('./features/maps/pages/MapPage'))
const ProfileDetailPage = lazy(
  () => import('./features/profile/pages/ProfileDetailPage'),
)
const ExploitationPage = lazy(
  () => import('./features/exploitations/pages/ExploitationPage'),
)
const ExploitationDetailPage = lazy(
  () => import('./features/exploitations/pages/ExploitationDetailPage'),
)
const ParcelPage = lazy(() => import('./features/parcels/pages/ParcelPage'))
const ParcelFormPage = lazy(
  () => import('./features/parcels/pages/ParcelFormPage'),
)
const ParcelDetailPage = lazy(
  () => import('./features/parcels/pages/ParcelDetailPage'),
)
const CultivationPage = lazy(
  () => import('./features/cultivations/pages/CultivationPage'),
)
const CultivationFormPage = lazy(
  () => import('./features/cultivations/pages/CultivationFormPage'),
)
const CultivationDetailPage = lazy(
  () => import('./features/cultivations/pages/CultivationDetailPage'),
)
const ToolPage = lazy(() => import('./features/tools/pages/ToolPage'))
const ToolDetailPage = lazy(
  () => import('./features/tools/pages/ToolDetailPage'),
)
const VehiclePage = lazy(() => import('./features/vehicles/pages/VehiclePage'))
const VehicleDetailPage = lazy(
  () => import('./features/vehicles/pages/VehicleDetailPage'),
)
const DevicePage = lazy(() => import('./features/devices/pages/DevicePage'))
const DeviceFormPage = lazy(
  () => import('./features/devices/pages/DeviceFormPage'),
)
const DeviceDetailPage = lazy(
  () => import('./features/devices/pages/DeviceDetailPage'),
)
const WarehousePage = lazy(
  () => import('./features/warehouses/pages/WarehousePage'),
)
const WarehouseDetailPage = lazy(
  () => import('./features/warehouses/pages/WarehouseDetailPage'),
)
const ActivityPage = lazy(
  () => import('./features/activities/pages/ActivityPage'),
)
const ActivityDetailPage = lazy(
  () => import('./features/activities/pages/ActivityDetailPage'),
)
const ActivityEditPage = lazy(
  () => import('./features/activities/pages/ActivityEditPage'),
)
const ActivityCreatePage = lazy(
  () => import('./features/activities/pages/ActivityCreatePage'),
)
const GuidePage = lazy(() => import('./features/help/pages/GuidePage'))
const FaqPage = lazy(() => import('./features/help/pages/FaqPage'))
const UpdatesPage = lazy(() => import('./features/help/pages/UpdatesPage'))
const CampaignPage = lazy(
  () => import('./features/campaigns/pages/CampaignPage'),
)
const CampaignDetailPage = lazy(
  () => import('./features/campaigns/pages/CampaignDetailPage'),
)
const SearchResultsPage = lazy(
  () => import('./features/search/pages/SearchResultsPage'),
)
const AdminDashboardPage = lazy(
  () => import('./features/admin/pages/AdminDashboardPage'),
)
const AdminUsersPage = lazy(
  () => import('./features/admin/pages/AdminUsersPage'),
)
const AdminUserDetailPage = lazy(
  () => import('./features/admin/pages/AdminUserAccountPage'),
)
const AdminExploitationDrillPage = lazy(
  () => import('./features/admin/pages/AdminExploitationDrillPage'),
)
const AdminParcelDrillPage = lazy(
  () => import('./features/admin/pages/AdminParcelDrillPage'),
)
const AdminCultivationDrillPage = lazy(
  () => import('./features/admin/pages/AdminCultivationDrillPage'),
)
const AdminAlertsPage = lazy(
  () => import('./features/admin/pages/AdminAlertsPage'),
)
const AdminAuditPage = lazy(
  () => import('./features/admin/pages/AdminAuditPage'),
)
const AdminSpeciesPage = lazy(
  () => import('./features/admin/pages/AdminSpeciesPage'),
)
const AdminVarietiesPage = lazy(
  () => import('./features/admin/pages/AdminVarietiesPage'),
)
const AdminInboxPage = lazy(
  () => import('./features/admin/pages/AdminInboxPage'),
)
const AdminFaqPage = lazy(() => import('./features/admin/pages/AdminFaqPage'))
const AdminEmailLogsPage = lazy(
  () => import('./features/admin/pages/AdminEmailLogsPage'),
)
const AdminTicketsPage = lazy(
  () => import('./features/admin/pages/AdminTicketsPage'),
)
const AdminTicketDetailPage = lazy(
  () => import('./features/admin/pages/AdminTicketDetailPage'),
)

const PrivateRoute = lazy(
  () => import('./features/auth/components/PrivateRoute'),
)
const AuthenticatedLayout = lazy(
  () => import('./shared/layout/AuthenticatedLayout'),
)

/* ─────────────  React-Query config  ───────────── */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min
      refetchOnWindowFocus: false,
      retry: 1,
      suspense: true,
    },
  },
})

/* ─────────────  Public layout  ───────────── */
const PublicLayout = ({ children }) => (
  <div className="layout-container">
    <main className="main-content">
      <ScrollToTop />
      {children}
    </main>
    <Footer />
  </div>
)

/* ─────────────  Router  ───────────── */
/* Layout helper: wraps children in ErrorBoundary + Suspense */
const LS = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
  </ErrorBoundary>
)

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <LS>
        <PublicLayout>
          <LandingPage />
        </PublicLayout>
      </LS>
    ),
  },
  {
    path: '/contact',
    element: (
      <LS>
        <PublicLayout>
          <ContactPage />
        </PublicLayout>
      </LS>
    ),
  },
  {
    path: '/login',
    element: (
      <LS>
        <PublicLayout>
          <LoginPage />
        </PublicLayout>
      </LS>
    ),
  },
  {
    path: '/terms',
    element: (
      <LS>
        <PublicLayout>
          <TermsPage />
        </PublicLayout>
      </LS>
    ),
  },
  {
    path: '/privacy',
    element: (
      <LS>
        <PublicLayout>
          <PrivacyPolicyPage />
        </PublicLayout>
      </LS>
    ),
  },
  {
    path: '/policy-privacy',
    element: (
      <LS>
        <PublicLayout>
          <PrivacyPolicyPage />
        </PublicLayout>
      </LS>
    ),
  },
  {
    path: '/policy-cookies',
    element: (
      <LS>
        <PublicLayout>
          <CookiesPolicyPage />
        </PublicLayout>
      </LS>
    ),
  },
  /* { path: '/register', element: <LS><PublicLayout><RegisterPage /></PublicLayout></LS> }, */

  /* All authenticated routes are nested under AuthenticatedLayout with Outlet */
  {
    element: (
      <LS>
        <AuthenticatedLayout />
      </LS>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'map',
        element: (
          <PrivateRoute>
            <MapPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'weather',
        element: (
          <PrivateRoute>
            <WeatherPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'prices',
        element: (
          <PrivateRoute>
            <PricesPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <PrivateRoute>
            <ProfileDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'exploitations',
        element: (
          <PrivateRoute>
            <ExploitationPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'exploitations/new',
        element: (
          <PrivateRoute>
            <ExploitationDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'exploitations/:id',
        element: (
          <PrivateRoute>
            <ExploitationDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'parcels',
        element: (
          <PrivateRoute>
            <ParcelPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'parcels/new',
        element: (
          <PrivateRoute>
            <ParcelFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'parcels/:id',
        element: (
          <PrivateRoute>
            <ParcelDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'parcels/:id/edit',
        element: (
          <PrivateRoute>
            <ParcelFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'cultivations',
        element: (
          <PrivateRoute>
            <CultivationPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'cultivations/new',
        element: (
          <PrivateRoute>
            <CultivationFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'cultivations/:id',
        element: (
          <PrivateRoute>
            <CultivationDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'cultivations/:id/edit',
        element: (
          <PrivateRoute>
            <CultivationFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'tools',
        element: (
          <PrivateRoute>
            <ToolPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'tools/new',
        element: (
          <PrivateRoute>
            <ToolDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'tools/:id',
        element: (
          <PrivateRoute>
            <ToolDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities',
        element: (
          <PrivateRoute>
            <ActivityPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities/new',
        element: (
          <PrivateRoute>
            <ActivityCreatePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities/:id',
        element: (
          <PrivateRoute>
            <ActivityDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities/:id/edit',
        element: (
          <PrivateRoute>
            <ActivityEditPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'vehicles',
        element: (
          <PrivateRoute>
            <VehiclePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'vehicles/new',
        element: (
          <PrivateRoute>
            <VehicleDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'vehicles/:id',
        element: (
          <PrivateRoute>
            <VehicleDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'devices',
        element: (
          <PrivateRoute>
            <DevicePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'devices/new',
        element: (
          <PrivateRoute>
            <DeviceFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'devices/:id',
        element: (
          <PrivateRoute>
            <DeviceDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'devices/:id/edit',
        element: (
          <PrivateRoute>
            <DeviceFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'warehouses',
        element: (
          <PrivateRoute>
            <WarehousePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'warehouses/new',
        element: (
          <PrivateRoute>
            <WarehouseDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'warehouses/:id',
        element: (
          <PrivateRoute>
            <WarehouseDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'campaigns',
        element: (
          <PrivateRoute>
            <CampaignPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'campaigns/:id',
        element: (
          <PrivateRoute>
            <CampaignDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'guide',
        element: (
          <PrivateRoute>
            <GuidePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'search',
        element: (
          <PrivateRoute>
            <SearchResultsPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'updates',
        element: (
          <PrivateRoute>
            <UpdatesPage />
          </PrivateRoute>
        ),
      },

      {
        path: 'faq',
        element: (
          <PrivateRoute>
            <FaqPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminDashboardPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminUsersPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/users/:uid',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminUserDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/users/:uid/exploitations/:eid',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminExploitationDrillPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/users/:uid/exploitations/:eid/parcels/:pid',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminParcelDrillPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/users/:uid/exploitations/:eid/parcels/:pid/cultivations/:cid',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminCultivationDrillPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/species',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminSpeciesPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/varieties',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminVarietiesPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/audit',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminAuditPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/inbox',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminInboxPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/faq',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminFaqPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/email-logs',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminEmailLogsPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/tickets',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminTicketsPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/tickets/:id',
        element: (
          <PrivateRoute requiredRole="ADMIN">
            <AdminTicketDetailPage />
          </PrivateRoute>
        ),
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
])

/* ─────────────  App  ───────────── */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AlertProvider>
          <HelpAssistantProvider>
            <NotificationProvider>
              <ThemeProvider>
                <ErrorBoundary>
                  <Suspense fallback={<LoadingScreen />}>
                    <GlobalAlert />
                    <RouterProvider router={router} />
                  </Suspense>
                </ErrorBoundary>
              </ThemeProvider>
            </NotificationProvider>
          </HelpAssistantProvider>
        </AlertProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
